import React from 'react';
import { billingApi } from 'services/businessBilling';
import { BusinessAction, BusinessSubmissionRecovery, BusinessSubmitRequest } from 'services/businessBilling/contract';
import { ParsedBusinessResult } from 'services/businessBilling/parseResponse';
import { BusinessErrorSource, toErrorSource } from '../components/BusinessError/errorDisplay';
import { CompanyCardData, toCompanyCardData } from '../components/CompanyCard/companyCardData';
import { BusinessLookupState, useBusinessLookup } from './useBusinessLookup';
import { BusinessSubmit, useBusinessSubmit } from './useBusinessSubmit';
import { useCooldown } from './useCooldown';
import { useLookupRetry } from './useLookupRetry';

export interface BusinessBillingOptions {
    tenantId?: string;
    token?: string;
    verificationToken: string | null;
    isCompany: boolean; // The Company journey is active; otherwise nothing is looked up.
    rawCui: string; // Exactly what the field shows.
    restored?: ParsedBusinessResult | null; // The attempt BOLT reported with the session (reload), already parsed.
    pendingSubmission?: boolean;
    recovery?: BusinessSubmissionRecovery | null;
    expiresAt?: string;
}

export interface BusinessBilling {
    lookup: BusinessLookupState;
    submit: BusinessSubmit;
    inFlight: boolean; // A business submit is being sent: the CUI field and the journey switch must be locked.
    pendingSubmission: boolean; // A saved or uncertain submission can only be retried unchanged.
    submitted: boolean; // The business submission was accepted.
    company: CompanyCardData | null; // The verified company, only while its verification is the one shown.
    localInvalid: boolean; // The CUI is locally invalid: only the inline message, no recovery panel.
    errorSource: BusinessErrorSource | null;
    actions: BusinessAction[]; // What the server permits next (never more than it sent).
    sessionLocked: boolean; // A session error: only starting again from the terminal is left.
    retryAvailableAt: number | null;
    retriesLeft: number;
    retry: () => void; // RETRY_LOOKUP: starts a new verification (the hook decides whether it is allowed).
}

// An unknown lookup outcome asks the customer to try again, so it offers the same as a provider failure.
const UNKNOWN_LOOKUP_ACTIONS: BusinessAction[] = ['RETRY_LOOKUP', 'SWITCH_TO_INDIVIDUAL', 'CONTINUE_WITHOUT_BILLING'];

const isSessionError = (result: ParsedBusinessResult | undefined): boolean =>
    result?.kind === 'failure' && result.response.outcome === 'session_error';

/**
 * Connects the Company journey to BOLT: debounced lookup, retry limits, submit with its idempotency key, and the
 * view the page needs (error, permitted actions, company card, locks). Pure composition of the single-purpose hooks;
 * it adds no rule of its own beyond choosing which answer is shown.
 */
export const useBusinessBilling = ({
    tenantId,
    token,
    verificationToken,
    isCompany,
    rawCui,
    restored = null,
    pendingSubmission = false,
    recovery = null,
    expiresAt,
}: BusinessBillingOptions): BusinessBilling => {
    // The session. A journey change needs no part in it: outside the Company journey the field is not read at all, so
    // the lookup restarts (with its pause) whenever the customer comes back.
    const scopeKey = `${tenantId ?? ''}|${token ?? ''}`;
    const cui = isCompany ? rawCui : '';

    // A restored attempt is shown as it is, with no new request, but only while the field still holds the CUI it belongs
    // to (empty for a failure, which carries none). The first change drops it for good: typing the old value back must
    // not revive an old answer. It has no authority of its own: Submit follows the same rules and the server re-checks.
    const restoredCui =
        recovery?.business.cui ?? (restored?.kind === 'eligible' ? restored.response.company.tax.cui : restored?.kind === 'failure' ? '' : null);
    const matchesNow = isCompany && restoredCui !== null && rawCui === restoredCui;
    const [restoreDropped, setRestoreDropped] = React.useState(false);
    React.useEffect(() => {
        if (restoredCui !== null && !matchesNow) {
            setRestoreDropped(true);
        }
    }, [restoredCui, matchesNow]);
    const dropRestore = React.useCallback(() => setRestoreDropped(true), []);
    const restoreActive = restored !== null && restoredCui !== null && !restoreDropped && matchesNow;

    const liveLookup = useBusinessLookup(
        pendingSubmission || restoreActive ? '' : cui,
        (verifiedCui) => {
            if (!tenantId || !token) {
                return Promise.reject(new Error('Missing billing session'));
            }
            return billingApi.lookup(tenantId, token, {
                verificationToken: verificationToken ?? '',
                taxCountryCode: 'RO',
                recipientType: 'BUSINESS',
                taxId: verifiedCui,
            });
        },
        scopeKey,
    );
    const lookup: BusinessLookupState =
        restoreActive && restored !== null && restoredCui !== null
            ? { phase: 'done', loading: false, cui: restoredCui, result: restored, retry: dropRestore }
            : liveLookup;

    // A saved submission is replayed only within its original session expiry, independent of lookup expiry.
    const recoveryUnexpired = useCooldown(Date.parse(expiresAt ?? ''));
    const submit = useBusinessSubmit({
        rawCui: cui,
        lookup,
        scopeKey,
        verificationToken: verificationToken ?? '',
        sessionUsable: !pendingSubmission || (recoveryUnexpired && Boolean(recovery && verificationToken)
            && !isSessionError(restored ?? undefined)),
        recovery,
        expiresAt,
        submit: (request: BusinessSubmitRequest) => {
            if (!tenantId || !token) {
                return Promise.reject(new Error('Missing billing session'));
            }
            return billingApi.submit(tenantId, token, request);
        },
    });

    const submissionPending = pendingSubmission || submit.pending;
    const recoveryExpired = submissionPending && !recoveryUnexpired;

    const submitResult = submit.result;
    const submitFailed = submitResult !== undefined && submitResult.kind !== 'submitted' && submitResult.kind !== 'eligible';
    const verificationRejected =
        submitResult?.kind === 'failure' && submitResult.response.error.code === 'VERIFICATION_STALE';

    const eligible = lookup.phase === 'done' && lookup.result?.kind === 'eligible' ? lookup.result.response : null;
    const company = React.useMemo(() => (eligible ? toCompanyCardData(eligible.company) : null), [eligible]);

    // The verification runs out at its `expiresAt` (re-rendered at that instant, no polling). An unreadable date counts
    // as expired, like in `canSubmit`. The server re-checks at submit, so the local clock is only a convenience.
    const stillValid = useCooldown(eligible ? Date.parse(eligible.verification.expiresAt) : null);
    const expired = eligible !== null && !stillValid;

    // An expired verification is a stale one: a new one is obtained with RETRY_LOOKUP.
    const lookupRetry = useLookupRetry({ lookup, scopeKey, verificationRejected: verificationRejected || expired, onRetry: lookup.retry });

    const submitted = submit.phase === 'submitted' || restored?.kind === 'submitted';
    const inFlight = submit.phase === 'in_flight';
    const sessionLocked = recoveryExpired || (lookup.phase === 'done' && isSessionError(lookup.result)) || isSessionError(submitResult);

    const localInvalid = isCompany && lookup.phase === 'invalid';

    // What is shown: the submit's answer for the verification now shown wins over the lookup's own answer.
    let shown: ParsedBusinessResult | undefined;
    if (isCompany && !submitted) {
        shown = submitFailed ? submitResult : lookup.phase === 'done' ? lookup.result : undefined;
    }
    // Said only while nothing else is going on: a submit in flight, a success or a submit answer decide first.
    const showExpired = expired && !submissionPending && !submitted && !inFlight && !submitFailed;
    const errorSource: BusinessErrorSource | null = recoveryExpired
        ? { kind: 'code', code: 'BILLING_SESSION_EXPIRED', field: null }
        : localInvalid
        ? { kind: 'code', code: 'CUI_INVALID_FORMAT', field: 'cui' }
        : showExpired
            ? { kind: 'code', code: 'VERIFICATION_STALE', field: 'cui' }
            : shown
                ? toErrorSource(shown)
                : null;

    let actions: BusinessAction[] = [];
    if (showExpired && eligible) {
        actions = ['RETRY_LOOKUP', ...eligible.allowedActions];
    } else if (errorSource && !localInvalid && shown) {
        if (shown.kind === 'failure') {
            const restoredFailure = restoreActive && shown === restored;
            // RETRY_LOOKUP only where a new verification is the way forward; a retryable submit failure is retried
            // by the Submit button instead.
            // A restored failure has no CUI to look up again.
            actions = (shown === submitResult && !verificationRejected) || restoredFailure
                ? shown.response.allowedActions.filter((action) => action !== 'RETRY_LOOKUP')
                : [...shown.response.allowedActions];
        } else if (shown.kind === 'unknown') {
            // Only the original Submit can resolve an uncertain submission.
            actions = shown === submitResult ? [] : UNKNOWN_LOOKUP_ACTIONS;
        }
    }

    // The backend has already accepted this selection. Only its exact retry or terminal restart remains applicable.
    if (submissionPending) actions = sessionLocked ? ['RESTART_FROM_TERMINAL'] : [];

    return {
        lookup,
        submit,
        inFlight,
        pendingSubmission: submissionPending,
        submitted,
        company: isCompany ? company : null,
        localInvalid,
        errorSource,
        actions,
        sessionLocked,
        retryAvailableAt: lookupRetry.retryAvailableAt,
        retriesLeft: lookupRetry.retriesLeft,
        retry: lookupRetry.retry,
    };
};
